/**
 * `/o/:org/admin` - members, invites, groups, policy, recovery and the audit log.
 *
 * V1 section 9 asks for members and invites, groups, an SSO and publishing
 * policy panel, audited ownership recovery and the audit log, and says the SSO
 * panel must show provider, connection state, test result, enforcement impact and
 * recovery contact, and that unavailable actions must be hidden rather than
 * shown greyed.
 *
 * Three of those panels cannot be built against this deployment, and the screen
 * says exactly which and why rather than filling them in:
 *
 *   SSO POSTURE. `app.sso_connections` has no read policy; its own table comment
 *   says clients cannot read it and that all access is through the `sso.*`
 *   functions. Those functions live in a schema that this deployment does not
 *   expose over the data API: `supabase/config.toml` lists `schemas = ["public",
 *   "graphql_public"]`. So the five facts V1:353 names cannot be read here.
 *
 *   The panel does not settle for one paragraph saying so. It renders the five
 *   as five labelled rows — provider, connection state, test result, enforcement
 *   impact, recovery contact — each carrying either the one value a browser can
 *   actually read (`app.client.config.signIn`, the deployment's own sign-in
 *   state, which is a different fact from the organization's IdP binding) or an
 *   explicit statement that the value was not read, naming the column that
 *   holds it. The no-read-policy argument moved OUT of the panel body and INTO a
 *   `<details>` labelled "Technical diagnostics", rendered for administrators
 *   only, per V1:388. No value is invented for any of the four unread rows.
 *
 *   SELF-REVIEW POLICY. Enforced, and enforced three times: `app.approve_draft`
 *   refuses the author of the draft outright (`0033_lifecycle_rpcs.sql:957-963`),
 *   the `reviews_insert` policy refuses a self-review the organization has not
 *   opted into (`0034_reviewer_separation.sql:85-90`), and `app.publish_draft`
 *   re-checks the flag before honouring an approval that relied on it
 *   (`0033:1398-1408`). Every use records `reviews.self_review` — what happened,
 *   not what was permitted — and audits `self_review` and `self_review_allowed`
 *   together, so an exception cannot be used quietly.
 *
 *   AND IT IS NOW SETTABLE, which it was not when this screen was written.
 *   `organizations.allow_self_review` was written by nothing in this repository
 *   — it defaulted false (`0001_schema.sql:144-145`) and the only way to reach
 *   the exception was a direct table write, which `0033:868-875` and
 *   `0033:2160-2163` both record as TODO-3. The panel therefore showed the value
 *   as a fact and said an operator had to write it.
 *
 *   `0061_self_review_policy_rpc.sql` closed that. The panel now offers an
 *   administrator a control that writes through `app.set_self_review_policy`:
 *   browser session only, org admin only, a required reason, an audit row
 *   carrying the previous value and the new one, and no ability to write any
 *   other field on the organization. It is the second half of TODO-3 — "a
 *   decision about who may enable it", answered by D17 — and not the whole of
 *   it, because D17 also settles that there is NO expiry in V1.
 *
 *   OWNERSHIP RECOVERY. Real, and offered by skill id with a required reason,
 *   through the audited route rather than a direct write.
 */

import type { AppShell, ScreenState } from '../app.js';
import { type Failure, type Result, ok } from '../api/client.js';
import * as api from '../api/queries.js';
import { type RawHtml, html, raw } from '../lib/html.js';
import { type PeopleDirectory, formatDateTime, personLabel } from '../lib/format.js';
import { button, docHead, failurePanel, presentFailure, skeletonRows, tag } from '../ui/components.js';

export interface AdminData {
  readonly members: readonly api.MembershipRow[];
  readonly invites: readonly api.InviteRow[];
  readonly groups: readonly api.GroupRow[];
  readonly audit: readonly api.AuditEventRow[];
  readonly isAdmin: boolean;
  readonly allowSelfReview: boolean;
  /**
   * Why the organization's self-review setting could not be read, or null
   * when it was.
   *
   * SEPARATE FROM `allowSelfReview`, because a setting this screen failed to
   * read is not a setting that is off, and rendering the second as the first
   * is how a panel ends up saying "Not allowed" about a policy somebody has
   * already enabled. The review screen reads the same value and would offer
   * the self-review path, so the two screens would disagree about one fact.
   */
  readonly selfReviewFailure: Failure | null;
  /**
   * Why the audit trail could not be read, or null when it was.
   *
   * Separate from `audit` because the two must not be renderable as each
   * other: `audit: []` means the organization did nothing, `auditFailure` means
   * this caller was not told. Collapsing the second into the first is what put
   * a `0` under the audit heading.
   */
  readonly auditFailure: Failure | null;
}

export async function loadAdmin(app: AppShell, orgId: string): Promise<Result<AdminData>> {
  const [members, invites, groups, organization] = await Promise.all([
    api.listMemberships(app.client, orgId),
    api.listInvites(app.client, orgId),
    api.listGroups(app.client, orgId),
    // THE ORGANIZATION'S OWN ROW, read for the one fact this panel shows and
    // the one the review screen asks about. It is read HERE rather than taken
    // from `app.state.organization`, which is read once when the tab signs in
    // and never refreshed: a tab that turned the exception ON and came back
    // to this screen would otherwise be told the policy was still off, while
    // the database says otherwise and the review screen says otherwise.
    api.getOrganization(app.client),
  ]);
  if (!members.ok) return members;
  if (!invites.ok) return invites;
  if (!groups.ok) return groups;

  const isAdmin = app.state.membership?.role === 'admin';
  let audit: readonly api.AuditEventRow[] = [];
  /**
   * A FAILED read and an EMPTY result are different facts and the panel has to
   * be able to tell them apart.
   *
   * `events.ok ? events.value : []` made a refused or failed audit read render
   * as the number `0` under the heading "Audit log" — measured, before this was
   * corrected, as `AUDIT-ON-FAILED-READ: 0 most recent`. On an audit trail that
   * is the worst possible invented value: it does not say "I could not read
   * this", it says "nothing happened". `listAuditEvents`' own comment
   * (`queries.ts:1247-1250`) says a short answer in this one listing "reads as
   * 'nothing happened'" and pages for 500 events precisely to avoid it; the
   * view then reintroduced the same reading by other means.
   */
  let auditFailure: Failure | null = null;
  if (isAdmin) {
    const events = await api.listAuditEvents(app.client, orgId, 100);
    if (events.ok) {
      audit = events.value;
    } else {
      auditFailure = events.failure;
    }
  }

  return ok({
    members: members.value,
    invites: invites.value,
    groups: groups.value,
    audit,
    isAdmin,
    allowSelfReview: organization.ok ? organization.value.allow_self_review : false,
    selfReviewFailure: organization.ok ? null : organization.failure,
    auditFailure,
  });
}

export function renderAdmin(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Admin' }], 'Administration')}
      <div class="panel">${skeletonRows(['Person', 'Role'], 4)}</div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Admin' }], 'Administration')}
      ${failurePanel(screen.failure, { retry: 'admin:reload' })}
    `;
  }

  const data = screen.data as AdminData;
  return html`
    ${docHead(
      [
        { label: 'Section', value: 'Admin' },
        { label: 'Organization', value: app.orgSlug },
        { label: 'Role', value: app.state.membership?.role ?? 'unknown' },
      ],
      'Administration',
      'Membership, invitations, groups, policy and the audit trail. Every change here is checked by the database as well as by this screen.',
    )}

    ${data.isAdmin ? '' : html`<div class="callout callout-attention">
      <span class="callout-tag">Read only</span>
      <p>
        You are not an administrator of this organization, so the roster, the pending invitations and
        the audit trail are not visible to you. That is a permission answer, not an empty organization.
      </p>
    </div>`}

    <div class="split">
      <div>
        ${membersPanel(app, data)}
        ${invitesPanel(app, data)}
      </div>
      <div>
        ${policyPanel(data)}
        ${ssoPanel(app, data)}
        ${recoveryPanel()}
        ${groupsPanel(data)}
      </div>
    </div>

    ${data.isAdmin ? auditPanel(app.state.directory, data) : ''}
  `;
}

function membersPanel(app: AppShell, data: AdminData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Members</h2>
        <span class="cell-meta">${data.members.length}</span>
      </div>
      <div class="table-scroll" data-scroll="members">
        <table class="data">
          <caption>Organization membership</caption>
          <thead>
            <tr>
              <th class="col-name" scope="col">Person</th>
              <th class="col-owner" scope="col">Role</th>
              <th class="col-status" scope="col">State</th>
              <th class="col-rev" scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            ${data.members.map(
              (member) => html`
                <tr>
                  <td><span class="cell-name">${personLabel(member.user_id, app.state.directory)}</span></td>
                  <td>${tag(member.role, 'R', member.role === 'admin' ? 'accent' : 'plain')}</td>
                  <td>${tag(member.state, member.state === 'active' ? 'A' : 'S', member.state === 'active' ? 'plain' : 'attention')}</td>
                  <td>
                    ${data.isAdmin
                      ? html`<div class="btn-row">
                          ${button(
                            member.role === 'admin' ? 'Make member' : 'Make admin',
                            `admin:role:${member.user_id}:${member.role === 'admin' ? 'member' : 'admin'}`,
                            'secondary',
                          )}
                          ${button('Remove', `admin:remove:${member.user_id}`, 'danger')}
                        </div>`
                      : html`<span class="cell-meta">${app.orgSlug}</span>`}
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function invitesPanel(app: AppShell, data: AdminData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Invitations</h2>
        ${data.isAdmin
          ? html`<div class="btn-row">
              <input class="field" id="invite-email" type="email" autocomplete="off" placeholder="person@example.com" aria-label="Email address to invite" />
              <select class="field" id="invite-role" aria-label="Role for the invitation">
                <option value="member">member</option>
                <option value="admin">admin</option>
              </select>
              ${button('Send invitation', 'admin:invite', 'primary')}
            </div>`
          : ''}
      </div>
      <div class="panel-body stack-sm">
        ${app.state.inviteLink === null
          ? ''
          : html`<div class="callout callout-attention">
              <span class="callout-tag">Shown once</span>
              <p>
                Only the hexadecimal SHA-256 of this token was stored, so the link cannot be shown
                again. Send it now.
              </p>
              <p><code>${app.state.inviteLink}</code></p>
            </div>`}
        ${data.invites.length === 0
          ? html`<p class="cell-note">No invitations are outstanding.</p>`
          : html`<ul class="file-list">
              ${data.invites.map(
                (invite) => html`<li>
                  <span>${invite.email} · ${invite.role}</span>
                  <span>expires ${formatDateTime(invite.expires_at)}</span>
                </li>`,
              )}
            </ul>`}
      </div>
    </div>
  `;
}

/**
 * The failure behind the self-review setting, or null when there was none.
 *
 * `AdminData` reaches this view as `ScreenState`'s `unknown` and is cast, so
 * an omitted key arrives as `undefined` and NOT as the `null` the type
 * promises. Comparing that `undefined` with `null` would make an absent
 * field look like a failure, and the panel would then say "not read" about a
 * value it had been handed — the same trap `review.ts`'s `bodyOf` and
 * `failureKindOf` guard against, for the same reason. So the KIND is read,
 * and an absent field is no failure.
 */
function selfReviewFailureOf(value: unknown): Failure | null {
  return typeof value === 'object' && value !== null ? (value as Failure) : null;
}

/**
 * Publishing policy.
 *
 * The self-review exception is a CONTROL here, not a readout, because
 * `0061_self_review_policy_rpc.sql` made it settable through an audited RPC
 * (`PUT /v1/orgs/:orgId/policy/self-review`). The callout this replaces said
 * the honest thing about a closed door — no route sets the flag and only a
 * direct table write could — and it is gone because the thing it warned about
 * is no longer true. `progress-v1.md:134` (D17, "an org admin may enable it")
 * and `docs/V1.md:353` (an admin "publishing policy" panel) both wanted this
 * control and neither could be satisfied by prose.
 *
 * Gating: the control renders only for an org admin, because V1 §9 requires
 * unavailable actions to be HIDDEN rather than shown greyed. That is a
 * presentation decision and NOT the authorization one — the RPC re-decides
 * `admin` from `authz.effective_org_role` and refuses anyone else, so hiding
 * it here is convenience and not the control. Hiding it AND enforcing it are
 * asserted separately, because a screen that only hides is the shape this
 * file was written to remove.
 *
 * AND IT IS HIDDEN WHEN THE VALUE COULD NOT BE READ. The control's effect is
 * decided by the CURRENT value — the button turns the setting off when it is
 * on and on when it is off — so a screen that did not read the value cannot
 * say which way the next press would go. Offering it anyway is offering a
 * write whose effect is unknown, and a panel that said "Not allowed" about a
 * policy somebody had already enabled is the same class of untruth the review
 * screen used to carry.
 */
function policyPanel(data: AdminData): RawHtml {
  const allow = data.allowSelfReview;
  // THE VALUE WAS NOT READ. That is not the same fact as "not allowed", and
  // the two must not be renderable as each other.
  const selfReviewFailure = selfReviewFailureOf(data.selfReviewFailure);
  const unreadNote = selfReviewFailure === null ? null : presentFailure(selfReviewFailure).body;
  const unread = unreadNote !== null;
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Publishing policy</h2></div>
      <div class="panel-body stack-sm">
        <dl class="dl">
          <dt>Self-review</dt>
          <dd>
            ${unread
              ? tag('Not read', '?', 'attention')
              : tag(allow ? 'Allowed' : 'Not allowed', allow ? 'Y' : 'N', allow ? 'attention' : 'plain')}
          </dd>
        </dl>
        ${
          unread
            ? html`<div class="callout callout-attention">
                <span class="callout-tag">Not read</span>
                <p>
                  The organization's self-review setting could not be read, so this panel does not
                  claim whether it is on and the control below is not offered. ${unreadNote}
                </p>
              </div>`
            : html`<p class="cell-note">
                ${allow
                  ? html`An author may approve their own draft. The database still records
                      <code>self_review</code> on the review row and audits the use, so the exception is
                      visible in the data and in the trail rather than merely available.`
                  : html`An author may not approve their own draft; somebody else holding
                      <code>manage</code> must review it.`}
              </p>`
        }
        ${
          unread
            ? ''
            : data.isAdmin
              ? html`
              <label class="field-label" for="self-review-allow">Allow self-review</label>
              <input
                class="field"
                id="self-review-allow"
                type="checkbox"
                ${raw(allow ? 'checked' : '')}
                aria-describedby="self-review-reason"
              />
              <label class="field-label" for="self-review-reason">Reason</label>
              <textarea
                class="field"
                id="self-review-reason"
                rows="3"
                aria-describedby="self-review-why"
                placeholder="Who is reviewing, and why the separation of duties does not apply"
              ></textarea>
              <p class="cell-note" id="self-review-why">
                Recorded with your name in the audit log, together with the previous setting and the
                new one.
              </p>
              <div class="btn-row">
                ${button(
                  allow ? 'Turn self-review off' : 'Allow self-review',
                  'admin:self-review',
                  allow ? 'secondary' : 'danger',
                )}
              </div>
            `
              : html`<div class="callout">
              <span class="callout-tag">Administrator only</span>
              <p>
                Only an administrator of this organization can change this setting, and every change
                is recorded in the audit log with the reason for it.
              </p>
            </div>`
        }
      </div>
    </div>
  `;
}

/**
 * THE SSO PANEL — V1 §9 (`docs/V1.md:353`) asks for five things by name:
 * "provider, connection state, test result, enforcement impact and recovery
 * contact." It used to get one callout paragraph that listed all five while
 * explaining that none could be shown, which satisfies a reader skimming for
 * the words and satisfies nobody who needs the facts. So there are five rows
 * now, and each one answers for itself.
 *
 * WHAT IS A VALUE AND WHAT IS A STATEMENT OF ABSENCE
 *   One thing on this screen is genuinely readable, and it is read the way
 *   `views/gate.ts:104` and `:205` read it — `app.client.config.signIn`, not a
 *   runtime global, because the product rename moved that global mid-session
 *   and hard-coding either spelling in a view is a bug waiting for the next
 *   rename. It is the DEPLOYMENT's sign-in state, which is a different fact
 *   from this organization's IdP binding, and the row says so rather than
 *   letting a reader take it for the binding.
 *
 *   The other four print no value. `app.sso_connections` carries `revoke all
 *   … from anon, authenticated` (`0003_sso.sql:2169`) with RLS enabled and
 *   zero policies, and `sso.*` lives in a schema this deployment does not
 *   expose over the data API (`supabase/config.toml:13`,
 *   `schemas = ["public", "graphql_public"]`). So the rows name the COLUMN that
 *   holds the fact — which tells the reader the fact exists and where an
 *   operator can look — and say the value was not read. Naming the column is
 *   not inventing the value. Printing a value would be, and the `tag()` on the
 *   one readable row is the only status claim on this panel.
 *
 * WHY THE DIAGNOSIS IS BEHIND `<details>` (V1 §9 :388)
 *   "Put technical diagnostics behind details for administrators." The
 *   no-read-policy argument is a real answer to "why is this empty", and it was
 *   rendering inline and unconditionally — measured on the live page before
 *   this change as `document.querySelectorAll('details').length === 0`, so the
 *   whole screen had no disclosure at all. It is now inside a `<details>`
 *   rendered only for `data.isAdmin`: a member sees the five rows and the
 *   honest "not read" against each, and does not receive a database-catalog
 *   argument about a table they cannot read either. Administrators get the
 *   argument on demand.
 *
 * NO CONTROL IS RENDERED, GREYED OR OTHERWISE (V1 §9 :353, "Hide unavailable
 * actions")
 *   There is no route on this deployment to test a connection or move an
 *   organization to enforced, so there is no button to disable. A greyed
 *   "Test connection" would advertise an action the server cannot perform.
 */
function ssoPanel(app: AppShell, data: AdminData): RawHtml {
  // The one SSO-adjacent fact a browser can read. See the header note: this is
  // the deployment's sign-in state, not the organization's IdP binding.
  const signIn = app.client.config.signIn;
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Single sign-on</h2></div>
      <div class="panel-body stack-sm">
        <dl class="dl">
          <dt>Provider</dt>
          <dd>
            ${tag(
              signIn.available ? 'Sign-in available' : 'Sign-in unavailable',
              signIn.available ? 'Y' : 'N',
              signIn.available ? 'accent' : 'attention',
            )}
            <span class="cell-note">
              This is the DEPLOYMENT's own sign-in control, not this organization's
              identity provider binding.
              ${signIn.available
                ? 'The server reports it is available.'
                : signIn.reason ?? 'The server named no identity provider.'}
              The organization's bound identity provider is column
              <code>provider_id</code> of <code>app.sso_connections</code>, with its kind in
              <code>provider_kind</code>. That row was not read here.
            </span>
          </dd>
          <dt>Connection state</dt>
          <dd>
            <span class="cell-note">
              Not read. Column <code>state</code> of <code>app.sso_connections</code>. No value is
              shown because none was received, so this page cannot tell you which state it is in.
            </span>
          </dd>
          <dt>Test result</dt>
          <dd>
            <span class="cell-note">
              Not read. Column <code>last_test_result</code> of <code>app.sso_connections</code>,
              with <code>last_tested_at</code> and <code>last_tested_by</code>. No value is shown
              because none was received.
            </span>
          </dd>
          <dt>Enforcement impact</dt>
          <dd>
            <span class="cell-note">
              Not read. Column <code>enforced_at</code> of <code>app.sso_connections</code> is
              stamped when the organization is moved to its most restrictive state. No value is
              shown because none was received, so this page cannot tell you whether sign-in is being
              enforced here.
            </span>
          </dd>
          <dt>Recovery contact</dt>
          <dd>
            <span class="cell-note">
              Not read. Column <code>idp_contact_email</code> of <code>app.sso_connections</code> is
              the support path the server returns when an SSO request is denied. It is not a fallback
              credential, and no address is shown because none was received.
            </span>
          </dd>
        </dl>
        <p class="cell-note">
          Four of these five are held in one table that this screen cannot read, by design. An
          operator with server credentials can read all of them.
        </p>
        ${data.isAdmin
          ? html`<details>
              <summary>Technical diagnostics</summary>
              <div class="callout">
                <span class="callout-tag">Why four rows are empty</span>
                <p>
                  Provider, connection state, test result, enforcement impact and recovery contact are
                  all held in <code>app.sso_connections</code> and reached through the
                  <code>sso</code> functions. That table has no read policy by design — its own table
                  comment says clients cannot read or write it directly
                  (<code>0001_schema.sql:744-746</code>), the grants revoke everything from
                  <code>anon</code> and <code>authenticated</code>
                  (<code>0003_sso.sql:2169</code>), and RLS is enabled with zero policies
                  (<code>0003_sso.sql:2178</code>). The <code>sso</code> functions are
                  <code>SECURITY DEFINER</code> and run as the owner, which is what makes zero
                  policies correct rather than an oversight. They are also in a schema this
                  deployment does not expose over the data API:
                  <code>supabase/config.toml:13</code> lists
                  <code>schemas = ["public", "graphql_public"]</code>.
                </p>
                <p>
                  The domain of each column, so an operator reading them knows what a value can be.
                  <code>provider_kind</code> is <code>saml</code> or <code>oidc</code>;
                  <code>state</code> is <code>disabled</code>, <code>testing</code> or
                  <code>enforced</code>; <code>last_test_result</code> is <code>success</code>,
                  <code>failure</code> or <code>error</code> (<code>0003_sso.sql:235-238</code>). An
                  organization may only be moved to enforced with a successful
                  <code>last_test_result</code> AND a <code>certificate_fingerprint</code>
                  (<code>0003_sso.sql:184-187</code>), and
                  <code>idp_contact_email</code> is returned as the support path when an SSO request
                  is denied — never as a fallback credential
                  (<code>0003_sso.sql:189-191</code>).
                </p>
              </div>
            </details>`
          : ''}
      </div>
    </div>
  `;
}

function recoveryPanel(): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Ownership recovery</h2></div>
      <div class="panel-body stack-sm">
        <p class="cell-note">
          Transfer a skill away from an owner who has left. The transfer is audited with your reason
          and cannot be done by editing the skill directly.
        </p>
        <label class="field-label" for="recovery-skill">Skill id</label>
        <input class="field" id="recovery-skill" autocomplete="off" placeholder="00000000-0000-0000-0000-000000000000" />
        <label class="field-label" for="recovery-owner">New owner id</label>
        <input class="field" id="recovery-owner" autocomplete="off" placeholder="00000000-0000-0000-0000-000000000000" />
        <label class="field-label" for="recovery-reason">Reason</label>
        <textarea class="field" id="recovery-reason" rows="3" placeholder="Who has left, and who takes the skill"></textarea>
        <div class="btn-row">${button('Transfer ownership', 'admin:recover', 'primary')}</div>
      </div>
    </div>
  `;
}

function groupsPanel(data: AdminData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Groups</h2>
        <span class="cell-meta">${data.groups.length}</span>
      </div>
      <div class="panel-body">
        ${data.groups.length === 0
          ? html`<p class="cell-note">No groups in this organization.</p>`
          : html`<ul class="file-list">
              ${data.groups.map((group) => html`<li><span>${group.name}</span><span>${group.slug}</span></li>`)}
            </ul>`}
      </div>
    </div>
  `;
}

/**
 * Who an audit row says acted — and this is not always a person.
 *
 * Measured on the live trail, grouped by `actor_kind`:
 *
 *     kind       rows   actor_id null   resolves in auth.users
 *     user       9449   0               587
 *     client     14374  14199          0
 *     system     572    573            0
 *     operator   388    6              0
 *
 * So three quarters of this table's rows carry a `client_grants` id or nothing
 * at all in `actor_id`. Passing those through `personLabel` — which is what
 * this cell did — printed "Unknown person" beside a client grant, which is a
 * category error rather than a missing name: it says a person was expected
 * where the trail recorded a machine. Hence the branch on the kind the row
 * itself carries, which is the only field here that says what kind of actor it
 * was.
 *
 * A `user` row that does not resolve is the other real case: someone who has
 * left the organization since, whose `auth.users` row is gone while their
 * history is not. `personLabel`'s fallback is the sentence for it.
 */
function auditActor(event: api.AuditEventRow, directory: PeopleDirectory): string {
  if (event.actor_kind === 'user') return personLabel(event.actor_id, directory);
  if (event.actor_kind === 'client') return 'Connected client';
  if (event.actor_kind === 'operator') return 'Recovery operator';
  return 'System';
}

function auditPanel(directory: PeopleDirectory, data: AdminData): RawHtml {
  // The branch is inside the panel, not beside it, so a caller who is an
  // administrator and whose read failed still gets a panel — one that says what
  // happened instead of a table over no rows.
  if (data.auditFailure !== null) return auditUnavailable(data);
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Audit log</h2>
        <span class="cell-meta">${data.audit.length} most recent</span>
      </div>
      <div class="table-scroll" data-scroll="audit">
        <table class="data">
          <caption>Organization audit trail, newest first</caption>
          <thead>
            <tr>
              <th class="col-name" scope="col">Action</th>
              <th class="col-owner" scope="col">Actor</th>
              <th class="col-status" scope="col">Result</th>
              <th class="col-when" scope="col">When</th>
            </tr>
          </thead>
          <tbody>
            ${data.audit.map(
              (event) => html`
                <tr>
                  <td>
                    <span class="cell-name">${event.action}</span>
                    <span class="cell-note">${event.target_kind} · ${event.target_id === null ? 'no target' : event.target_id.slice(0, 8)}</span>
                  </td>
                  <td class="cell-meta">${auditActor(event, directory)}</td>
                  <td>${tag(event.result, event.result === 'success' ? 'S' : 'D', event.result === 'success' ? 'accent' : 'danger')}</td>
                  <td class="cell-meta">${formatDateTime(event.occurred_at)}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    </div>
  `;
}

function auditUnavailable(data: AdminData): RawHtml {
  // Reached only by an administrator: `renderAdmin:139` renders no audit panel
  // at all for anyone else, so this branch cannot become a second way of
  // learning that the trail exists but is hidden.
  //
  // The number is not shown because no number was received. `${data.audit.length}`
  // under this heading would be `0`, and `0 most recent` on an audit trail is a
  // claim that nothing happened.
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Audit log</h2>
        <span class="cell-meta">not read</span>
      </div>
      <div class="panel-body">
        ${failurePanel(data.auditFailure ?? { kind: 'server', detail: '', requestId: null }, {
          retry: 'admin:reload',
        })}
        <p class="cell-note">
          No count is shown because none was received. An empty audit trail and an audit trail this
          page could not read are different facts, and only one of them means nothing happened.
        </p>
      </div>
    </div>
  `;
}